import { ChevronRight, ArrowLeft } from "lucide-react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { formatCOP } from "../../../lib/api";

export const VendedoresTab = ({ items, onDrill }) => {
  const max = Math.max(1, ...items.map((v) => v.total || 0));
  if (!items.length) {
    return (
      <Card className="p-12 text-center bg-white border-cm-border shadow-none">
        <p className="text-sm text-cm-ink-soft">Sin actividad de vendedores en este período.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="vendedores-list">
      {items.map((v, idx) => {
        const pct = (v.total / max) * 100;
        return (
          <Card
            key={`${v.vendedor_id || "none"}-${idx}`}
            className="bg-white border-cm-border shadow-none overflow-hidden cursor-pointer hover:border-cm-red transition-colors"
            onClick={() => v.vendedor_id && onDrill(v.vendedor_id)}
            data-testid={`vendedor-row-${idx + 1}`}
          >
            <div className="flex items-center gap-4 px-5 py-3.5">
              <div className="w-10 flex-shrink-0 text-center font-mono text-cm-ink-soft font-medium">#{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium truncate">{v.nombre}</p>
                  {v.cargo && <span className="text-[10px] uppercase tracking-widest text-cm-ink-soft">· {v.cargo}</span>}
                </div>
                <div className="mt-1.5 h-1.5 bg-cm-cream-alt rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cm-yellow to-cm-red" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1.5 flex items-center gap-3 text-[11px] text-cm-ink-soft flex-wrap">
                  <span>{v.pedidos} pedidos</span>
                  <span>·</span>
                  <span>Portal: {v.pedidos_portal} ({formatCOP(v.total_portal)})</span>
                  <span>·</span>
                  <span>Presencial: {v.pedidos_presencial} ({formatCOP(v.total_presencial)})</span>
                  <span>·</span>
                  <span>Ticket prom: {formatCOP(v.ticket_promedio)}</span>
                  {v.nc_count > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-cm-red">NC: −{formatCOP(v.nc_total)} ({v.nc_count})</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-mono text-lg font-bold">{formatCOP(v.total)}</p>
                {v.vendedor_id && (
                  <p className="text-[10px] uppercase tracking-widest text-cm-red flex items-center justify-end mt-0.5">
                    Ver detalle <ChevronRight className="h-3 w-3" />
                  </p>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export const VendedorDetail = ({ data, onBack }) => (
  <div className="space-y-4" data-testid="vendedor-detail">
    <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2" data-testid="vendedor-back">
      <ArrowLeft className="h-4 w-4 mr-1" /> Volver al ranking
    </Button>
    <Card className="bg-white border-cm-border shadow-none p-6">
      <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Vendedor</p>
      <h2 className="font-serif text-2xl mt-1">{data.nombre}</h2>
      {data.cargo && <p className="text-xs text-cm-ink-soft mt-1">{data.cargo}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
        <div><p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Ventas</p><p className="font-mono text-xl font-bold">{formatCOP(data.kpi.total)}</p>{data.kpi.nc_total > 0 && (<p className="text-[10px] text-cm-red mt-0.5">Bruto {formatCOP(data.kpi.total_bruto)} − NC {formatCOP(data.kpi.nc_total)} ({data.kpi.nc_count})</p>)}</div>
        <div><p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Pedidos</p><p className="font-mono text-xl font-bold">{data.kpi.count}</p></div>
        <div><p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Ticket prom</p><p className="font-mono text-xl font-bold">{formatCOP(data.kpi.ticket_promedio)}</p></div>
        <div><p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Portal / Presencial</p><p className="font-mono text-sm">{formatCOP(data.kpi.portal.total)} / {formatCOP(data.kpi.presencial.total)}</p></div>
      </div>
    </Card>

    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="bg-white border-cm-border shadow-none p-5" data-testid="vendedor-top-clientes">
        <h3 className="font-serif text-base mb-3">Top clientes</h3>
        {data.top_clientes.length === 0 ? <p className="text-sm text-cm-ink-soft">Sin clientes en el período.</p> : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-cm-border">
              {data.top_clientes.slice(0, 10).map((c, i) => (
                <tr key={c.cliente_id || i}>
                  <td className="py-2"><span className="font-mono text-cm-ink-soft text-xs">{i + 1}.</span> {c.nombre}</td>
                  <td className="py-2 text-right text-xs text-cm-ink-soft">{c.pedidos} ped</td>
                  <td className="py-2 text-right font-mono font-semibold">{formatCOP(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="bg-white border-cm-border shadow-none p-5" data-testid="vendedor-top-marcas">
        <h3 className="font-serif text-base mb-3">Top marcas</h3>
        {data.top_marcas.length === 0 ? <p className="text-sm text-cm-ink-soft">Sin marcas en el período.</p> : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-cm-border">
              {data.top_marcas.slice(0, 8).map((m, i) => (
                <tr key={m.marca}>
                  <td className="py-2"><span className="font-mono text-cm-ink-soft text-xs">{i + 1}.</span> {m.marca}</td>
                  <td className="py-2 text-right text-xs text-cm-ink-soft">{m.cantidad.toFixed(0)} und</td>
                  <td className="py-2 text-right font-mono font-semibold">{formatCOP(m.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>

    <Card className="bg-white border-cm-border shadow-none p-5" data-testid="vendedor-top-productos">
      <h3 className="font-serif text-base mb-3">Top productos</h3>
      {data.top_productos.length === 0 ? <p className="text-sm text-cm-ink-soft">Sin productos en el período.</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft border-b border-cm-border">
            <tr><th className="py-2 pr-2">#</th><th className="py-2">Producto</th><th className="py-2 text-right">Cant.</th><th className="py-2 text-right">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-cm-border">
            {data.top_productos.map((p, i) => (
              <tr key={p.producto_id || i}>
                <td className="py-2 pr-2 font-mono text-cm-ink-soft text-xs">{i + 1}</td>
                <td className="py-2"><div className="font-medium">{p.nombre}</div><div className="text-[11px] text-cm-ink-soft">{p.marca}</div></td>
                <td className="py-2 text-right font-mono">{p.cantidad.toFixed(0)}</td>
                <td className="py-2 text-right font-mono font-semibold">{formatCOP(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>

    <Card className="bg-white border-cm-border shadow-none p-5" data-testid="vendedor-by-proveedor">
      <h3 className="font-serif text-base mb-1">Venta por proveedor</h3>
      <p className="text-xs text-cm-ink-soft mb-3">Total facturado y a cuántos clientes únicos llegaste con cada proveedor en el período.</p>
      {!data.by_proveedor?.length ? <p className="text-sm text-cm-ink-soft">Sin ventas por proveedor en el período.</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft border-b border-cm-border">
            <tr>
              <th className="py-2 pr-2">#</th>
              <th className="py-2">Proveedor</th>
              <th className="py-2 text-right">Clientes</th>
              <th className="py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cm-border">
            {data.by_proveedor.map((p, i) => (
              <tr key={p.proveedor}>
                <td className="py-2 pr-2 font-mono text-cm-ink-soft text-xs">{i + 1}</td>
                <td className="py-2 font-medium">{p.proveedor}</td>
                <td className="py-2 text-right font-mono">{p.clientes_unicos}</td>
                <td className="py-2 text-right font-mono font-semibold">{formatCOP(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>

    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="bg-white border-cm-border shadow-none p-5" data-testid="vendedor-daily">
        <h3 className="font-serif text-base mb-1">Venta diaria (período)</h3>
        <p className="text-xs text-cm-ink-soft mb-3">Facturas realizadas y valor total por día.</p>
        {!data.daily?.length ? <p className="text-sm text-cm-ink-soft">Sin actividad diaria en el período.</p> : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft border-b border-cm-border sticky top-0 bg-white">
                <tr>
                  <th className="py-2">Fecha</th>
                  <th className="py-2 text-right">Facturas</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {[...data.daily].reverse().map((d) => (
                  <tr key={d.fecha}>
                    <td className="py-2 font-mono text-xs">{d.fecha}</td>
                    <td className="py-2 text-right font-mono">{d.facturas}</td>
                    <td className="py-2 text-right font-mono font-semibold">{formatCOP(d.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="bg-white border-cm-border shadow-none p-5" data-testid="vendedor-monthly">
        <h3 className="font-serif text-base mb-1">Venta mensual (últimos 12 meses)</h3>
        <p className="text-xs text-cm-ink-soft mb-3">Independiente del filtro de período seleccionado arriba.</p>
        {!data.monthly?.length ? <p className="text-sm text-cm-ink-soft">Sin ventas registradas en los últimos 12 meses.</p> : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-cm-ink-soft border-b border-cm-border">
              <tr>
                <th className="py-2">Mes</th>
                <th className="py-2 text-right">Facturas</th>
                <th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cm-border">
              {[...data.monthly].reverse().map((m) => (
                <tr key={m.mes}>
                  <td className="py-2 font-mono text-xs">{m.mes}</td>
                  <td className="py-2 text-right font-mono">{m.facturas}</td>
                  <td className="py-2 text-right font-mono font-semibold">{formatCOP(m.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  </div>
);
